import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from 'lucide-react';
import { supabase, emailConfirmationService } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { clearPendingVerificationEmail, getPendingVerificationEmail } from '../utils/sessionStorage';
import toast from 'react-hot-toast';

const EmailConfirmationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshAuth, loading } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'already_confirmed'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  /**
   * Redirects user to appropriate page based on their role and profile completion
   */
  const redirectToRolePage = async (userId: string) => {
    if (redirecting) return; // Prevent multiple redirects

    setRedirecting(true);
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        navigate('/matchmaking');
        return;
      }

      if (userProfile?.role === 'cdmo') {
        const { dbService } = await import('../lib/supabase');

        // Check for existing profile (could be auto-claimed by trigger)
        const profileStatus = await dbService.checkCDMOProfileCompletion(userId);

        if (profileStatus.hasProfile) {
          if (profileStatus.isComplete) {
            // Profile exists and is complete, go to dashboard
            navigate('/cdmo-dashboard');
          } else {
            // Profile exists but incomplete, go to EDIT mode
            const existingProfile = await dbService.getCDMOProfile(userId);
            if (existingProfile) {
              // navigate(`/cdmo-application?edit=${existingProfile.id}`);
              navigate('/cdmo-application');
            } else {
              navigate('/cdmo-application');
            }
          }
        } else {
          // No profile yet, go to create new
          navigate('/cdmo-application');
        }
      }
      else {
        navigate('/matchmaking');
      }
    } catch (error) {
      console.error('Error determining redirect destination:', error);
      navigate('/matchmaking');
    }
  };

  useEffect(() => {
    // Wait for auth to load before processing
    if (loading) return;

    // Check if user is already verified and logged in
    if (user?.email_confirmed_at) {
      setStatus('already_confirmed');

      // Show success message briefly before redirecting
      setTimeout(() => {
        redirectToRolePage(user.id);
      }, 2000);
      return;
    }

    const handleEmailConfirmation = async () => {
      try {
        // Handle both URL query parameters and hash fragments
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));

        // Get parameters from either query string or hash fragment
        const getParam = (key: string) => urlParams.get(key) || hashParams.get(key) || searchParams.get(key);

        const tokenHash = getParam('token_hash') || getParam('token');
        const type = getParam('type');
        const accessToken = getParam('access_token');
        const refreshToken = getParam('refresh_token');
        const error = getParam('error');
        const errorDescription = getParam('error_description');
        // If user is already verified and no parameters, show success
        if (!tokenHash && !accessToken && !error && user?.email_confirmed_at) {
          setStatus('already_confirmed');
          return;
        }

        // Special handling for Supabase redirect without parameters but user is verified
        if (!tokenHash && !accessToken && !error && user && user.email_confirmed_at) {
          setStatus('success');

          setTimeout(() => {
            const redirectToRolePage = async () => {
              try {
                const { data: userProfile } = await supabase
                  .from('users')
                  .select('role')
                  .eq('id', user.id)
                  .single();

                if (userProfile?.role === 'cdmo') {
                  navigate('/cdmo-application');
                } else {
                  navigate('/matchmaking');
                }
              } catch (error) {
                console.error('Error fetching user profile:', error);
                navigate('/matchmaking');
              }
            };

            redirectToRolePage();
          }, 1500);
          return;
        }
        // Handle error cases first
        if (error) {
          setStatus('error');
          setError(errorDescription || 'Email confirmation failed. Please try again.');
          return;
        }

        // If no parameters and user is not verified, show error
        if (!tokenHash && !accessToken && !user?.email_confirmed_at) {
          setStatus('error');
          setError('Invalid confirmation link. Please check your email and try again.');
          return;
        }

        // Handle different URL formats from Supabase
        if (accessToken && refreshToken) {
          // Handle session-based confirmation (newer Supabase format)
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            setStatus('error');
            setError('Failed to confirm email. Please try again or contact support.');
            return;
          }

          if (sessionData.user?.id) {
            setStatus('success');
            if (!hasShownSuccessToast) {
              toast.success('Email confirmed successfully! Welcome to Pharma Connect Navigator.');
              setHasShownSuccessToast(true);
            }

            // Refresh auth state to update email verification status
            await refreshAuth();

            // Read any pending verification email stored during signup/login flow
            const pendingEmail = getPendingVerificationEmail();

            // Redirect after successful confirmation
            setTimeout(async () => {
              try {
                const uid = sessionData.user?.id;
                const userEmail = sessionData.user?.email || pendingEmail || undefined;

                if (uid && userEmail) {
                  try {
                    const { dbService } = await import('../lib/supabase');

                    // Retrieve potential tokens and metadata from URL
                    const token = getParam('token') || undefined;

                    // Note: Transfer/claim logic is now handled during sign up in StartupSignup.tsx
                    // This prevents duplicate processing and RLS policy conflicts

                    // Try to claim any admin-created CDMO application for this email or token
                    const claimedId = await dbService.claimCDMOApplication(uid, userEmail, token);
                    if (claimedId) {
                      try { clearPendingVerificationEmail(); } catch (e) { }
                      navigate('/cdmo-application', { replace: true });
                      return;
                    }

                    // ── 3. FALLBACK FORCE-CLAIM ─────────────────────────
                    // Try to locate an admin-created application and force-claim it by id
                    const adminApp = await dbService.getAdminCreatedData(userEmail);
                    if (adminApp?.id) {
                      const forced = await dbService.forceClaimApplicationById(adminApp.id, uid, userEmail);
                      if (forced) {
                        try { clearPendingVerificationEmail(); } catch (e) { }
                        navigate('/cdmo-application', { replace: true });
                        return;
                      }
                    }
                  } catch (claimErr) {
                    // Don't block the redirect — the account itself exists — but tell the
                    // user their claim didn't complete instead of silently landing them on
                    // a blank CDMO application with no explanation (client-reported bug).
                    console.warn('Auto-claim after confirmation failed:', claimErr);
                    toast.error(claimErr instanceof Error ? claimErr.message : 'We could not complete your CDMO profile claim. Please contact support.');
                  }
                }

                if (sessionData.user?.id) {
                  try { clearPendingVerificationEmail(); } catch (e) { }
                  redirectToRolePage(sessionData.user.id);
                } else {
                  navigate('/matchmaking');
                }
              } catch (err) {
                console.error('Error post-confirmation redirect:', err);
                navigate('/matchmaking');
              }
            }, 2000);
          }
          return;
        }

        // Handle token-based confirmation (older format or manual verification)
        if (!tokenHash) {
          setStatus('error');
          setError('Invalid confirmation link. Please check your email and try again.');
          return;
        }

        // Verify the email confirmation token
        const { data: tokenData, error: confirmError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: (type as 'signup') || 'signup'
        });

        if (confirmError) {
          if (confirmError.message.includes('expired')) {
            setStatus('error');
            setError('This confirmation link has expired. Please request a new one.');
          } else if (confirmError.message.includes('already confirmed')) {
            setStatus('already_confirmed');
            setTimeout(() => {
              // Use authenticated user if available, otherwise fallback to matchmaking
              if (user?.id) {
                redirectToRolePage(user.id);
              } else {
                navigate('/matchmaking');
              }
            }, 2000);
          } else {
            setStatus('error');
            setError('Failed to confirm email. Please try again or contact support.');
          }
          return;
        }

        if (tokenData?.user?.id) {
          setStatus('success');
          if (!hasShownSuccessToast) {
            toast.success('Email confirmed successfully! Welcome to Pharma Connect Navigator.');
            setHasShownSuccessToast(true);
          }

          // Refresh auth state to update email verification status
          await refreshAuth();

          // Read any pending verification email stored during signup/login flow
          const pendingEmail = getPendingVerificationEmail();

          // Redirect to appropriate page after successful confirmation
          setTimeout(async () => {
            try {
              const uid = tokenData?.user?.id;
              const userEmail = tokenData?.user?.email || pendingEmail || undefined;
              if (uid && userEmail) {
                try {
                  const { dbService } = await import('../lib/supabase');

                  // Retrieve potential tokens and metadata
                  const token = getParam('token') || undefined;

                  // Note: Transfer/claim logic is now handled during sign up in StartupSignup.tsx
                  // Try to claim any admin-created CDMO application for this email or token
                  const claimedId = await dbService.claimCDMOApplication(uid, userEmail, token);
                  if (claimedId) {
                    try { clearPendingVerificationEmail(); } catch (e) { /* ignore */ }
                    navigate('/cdmo-application', { replace: true });
                    return;
                  }
                } catch (claimErr) {
                  // Don't block the redirect — the account itself exists — but tell the
                  // user their claim didn't complete instead of silently landing them on
                  // a blank CDMO application with no explanation (client-reported bug).
                  console.warn('Auto-claim after confirmation failed:', claimErr);
                  toast.error(claimErr instanceof Error ? claimErr.message : 'We could not complete your CDMO profile claim. Please contact support.');
                }
              }

              if (uid) {
                try { clearPendingVerificationEmail(); } catch (e) { /* ignore */ }
                redirectToRolePage(uid);
              }
            } catch (err) {
              console.error('Error post-confirmation redirect:', err);
              navigate('/matchmaking');
            }
          }, 2000);
        }
      } catch (error: unknown) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setError('An unexpected error occurred. Please try again.');
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate, refreshAuth, loading, user, hasShownSuccessToast, redirecting]);

  const handleResendConfirmation = async () => {
    if (!user?.email) {
      toast.error('No email address found. Please sign up again.');
      navigate('/startup-signup');
      return;
    }

    setIsResending(true);
    try {
      await emailConfirmationService.resendConfirmationEmail(user.email);
      toast.success('Confirmation email sent! Please check your inbox.');
    } catch (error: unknown) {
      console.error('Error resending confirmation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('rate limit')) {
        toast.error('Please wait before requesting another confirmation email.');
      } else {
        toast.error('Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <Loader2 className="w-16 h-16 text-electric-blue mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-charcoal mb-4">Confirming Your Email</h1>
            <p className="text-slate-gray">Please wait while we verify your email address...</p>
          </motion.div>
        );

      case 'success':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-charcoal mb-4">Email Confirmed Successfully!</h1>
            <p className="text-slate-gray mb-6">
              Your email has been verified. You'll be redirected to your dashboard shortly.
            </p>
            <div className="flex items-center justify-center text-sm text-slate-gray">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Redirecting...
            </div>
          </motion.div>
        );

      case 'already_confirmed':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-charcoal mb-4">Email Confirmed</h1>
            <p className="text-slate-gray mb-6">
              Your email address has been verified. Redirecting you to your dashboard...
            </p>
            <div className="flex items-center justify-center text-sm text-slate-gray">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Redirecting...
            </div>
          </motion.div>
        );

      case 'error':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-charcoal mb-4">Email Confirmation Failed</h1>
            <p className="text-slate-gray mb-6">{error}</p>

            <div className="space-y-4">
              {user?.email && (
                <button
                  onClick={handleResendConfirmation}
                  disabled={isResending}
                  className="bg-electric-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-electric-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Confirmation Email
                    </>
                  )}
                </button>
              )}

              <button
                onClick={() => navigate('/startup-signup')}
                className="text-electric-blue hover:text-electric-blue/80 transition-colors font-medium"
              >
                Back to Sign Up
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md border border-white/50">
        <div className="flex items-center justify-center mb-8">
          <Mail className="w-8 h-8 text-electric-blue mr-3" />
          <span className="text-xl font-bold text-charcoal">Pharma Connect Navigator</span>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default EmailConfirmationPage;
